import { defineStore, acceptHMRUpdate } from "pinia";
import Api from "src/utils/api";
import Constants from "src/utils/constants";
import Helper from "../utils/helper";

export const useFormTemplateResponseStore = defineStore(
  "form_template_response_store",
  {
    persist: true,
    state: () => ({
      templateResponseList: [],
      totalForms: 0,
      response: {},
      templateId: null,
      statusFilterList: []
    }),
    getters: {
      getResponse: (state) => state.response
    },
    actions: {
      async resetResponse() {
        this.response = {};
      },
      async setResponse(key, value) {
        // console.log("SET RESPONSE :", key, value);
        // console.warn("SET RESPONSE call stack (caller trace):", new Error().stack);
        // Guard: avoid creating a literal "undefined" key when key is undefined/null (e.g. missing field id)
        if (value !== undefined && (key === undefined || key === null)) {
          console.error(
            "setResponse: key is undefined or null (would create key 'undefined'). Ignoring. Call stack:",
            new Error().stack
          );
          return;
        }
        // if the key is an object and value is undefined, then set the response to the key
        if (typeof key === "object" && value === undefined) {
          this.response = key;
          const keyCount = Object.keys(this.response).length;
          const hasUndefinedKey = Object.prototype.hasOwnProperty.call(
            this.response,
            "undefined"
          );
          console.log(
            "REPLACING THE RESPONSE OBJECT :",
            this.response,
            "| top-level keys:",
            keyCount
          );
          if (hasUndefinedKey) {
            console.error(
              "REPLACING THE RESPONSE OBJECT: payload has literal key 'undefined' (object was likely built with undefined as key, e.g. field.id).",
              this.response
            );
          }
          if (keyCount > 1) {
            console.error(
              "REPLACING THE RESPONSE OBJECT: expected at most 1 top-level key, got",
              keyCount,
              this.response
            );
          }
          return;
        }

        // check if the value already exists
        const doesValueExist = Helper.findAndSetKeyInObject(
          Helper.deepCopy(this.response),
          key
        );
        // if it exists then update the value
        if (doesValueExist) {
          const result = Helper.findAndSetKeyInObject(
            Helper.deepCopy(this.response),
            key,
            value
          );
          this.response = result;
        } else {
          // QA/QC procedures: key may be a list-item id (stored as obj.id, not as obj[key]).
          // If so, set the value on that list-item object instead of adding a new root key.
          const updated = Helper.deepCopy(this.response);
          if (Helper.findObjectByIdAndSetKey(updated, key, key, value)) {
            this.response = updated;
            return;
          }
          // Only allow adding a new top-level key when it is the form template id.
          // This prevents list-item ids (or other UUIDs) from being added at root on load or
          // when selectedPath is missing, which would pollute the response and not match Supabase.
          if (key !== this.templateId) {
            return;
          }
          // if it doesn't exist then create a new object with the key and value
          console.error(
            "CREATING A NEW OBJECT WITH THE KEY AND VALUE :",
            key,
            value
          );
          this.response = {
            ...this.response,
            [key]: value
          };
        }
      },

      /**
       * Path-based response update — avoids DFS entirely.
       * @param {string[]} pathArray - e.g. [templateId, pageId, itemId]
       * @param {*} value - the value to set at that path
       */
      setResponseByPath(pathArray, value) {
        if (!Array.isArray(pathArray) || pathArray.length === 0) {
          console.error(
            "setResponseByPath: pathArray is empty or invalid",
            pathArray
          );
          return;
        }
        const pathString = pathArray.join(".");
        this.response = Helper.setValueByPath(this.response, pathString, value);
      },

      async getTotalSubmissionsCount(templateId) {
        return await Api.FormTemplatesResponse.getTotalSubmissionsCount([
          {
            field: "form_template_id",
            value: templateId
          }
        ]);
      },
      async setInitialResponse(response) {
        this.response = response;
      },
      async getTemplatesResponseList(
        config,
        customTableName = null,
        offlineFirst = true
      ) {
        let _res = await Api.FormTemplatesResponse.getFormTemplatesReponseList(
          config,
          customTableName,
          offlineFirst
        );
        this.totalForms = _res.totalItems || 0;
        this.templateResponseList = _res.data || [];
        return _res;
      },
      /**
       * The newest response a form has, or null when it has none. Online, and
       * leaves `templateResponseList` alone.
       *
       * @param {string} formTemplateId
       * @returns {Promise<Object|null>} the response jsonb, not the row
       */
      async getLatestResponse(formTemplateId) {
        const res = await Api.FormTemplatesResponse.getFormTemplatesReponseList(
          {
            queryParams: [
              {
                field: "form_template_id",
                value: formTemplateId,
                opType: Constants.enums.COMPARISON_TYPE.OR
              }
            ],
            sortField: "created_at",
            sortOrder: -1,
            page: 0,
            rows: 1
          },
          null,
          false
        );
        if (res?.error) throw res.error;
        return res?.data?.[0]?.response ?? null;
      },
      async getFormTemplateResponseById(
        id,
        fromLocal = false,
        offlineFirst = true
      ) {
        if (fromLocal == true) {
          const existingResponse = this.templateResponseList.find(
            (item) => item.id === id
          );
          if (existingResponse) {
            return existingResponse;
          }
        }
        return await Api.FormTemplatesResponse.getFormTemplateResponseById(
          id,
          offlineFirst
        );
      },
      async getResponseHistory(responseId) {
        return await Api.FormTemplatesResponse.getResponseHistory(responseId);
      },
      async setFormTemplateResponse(data, offlineFirst = true) {
        console.log("SET FORM TEMPLATE RESPONSE :", data);
        console.log("SET FORM TEMPLATE RESPONSE OFFLINE FIRST :", offlineFirst);
        return await Api.FormTemplatesResponse.setFormTemplateResponse(
          data,
          offlineFirst
        );
      },
      async updateEmailHistory(logData, responseId) {
        try {
          const { data, error } =
            await this.getFormTemplateResponseById(responseId);
          if (error) throw error;

          const updated = {
            ...data,
            email_history: [...(data.email_history || []), logData]
          };

          return await this.setFormTemplateResponse(updated);
        } catch (error) {
          console.error("Failed to append email history:", error);
          return { error: error.message || "Failed to save email history" };
        }
      },
      async getDistinctValues(columnName, offlineFirst = true) {
        const { data, error } =
          await Api.FormTemplatesResponse.getDistinctValues(
            [columnName],
            offlineFirst
          );
        if (error) {
          throw error;
        }
        return data
          .map((item) => {
            const value = Object.values(item)[0];
            return value ? { label: value, value } : null;
          })
          .filter(Boolean);
      }
    },
    $reset() {}
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useFormTemplateResponseStore, import.meta.hot)
  );
}
